import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import ForfeitureReasonTable from '../../../components/tables/ForfeitureReasonTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useForfeitureReasonSearch, useDeleteForfeitureReason } from '../../../hooks/useForfeitureReasonApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { ForfeitureReasonDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const ForfeitureReasonList: React.FC = () => {
  const [formFilters, setFormFilters] = useState<any>({}); // Form input values
  const [searchFilters, setSearchFilters] = useState<any>({}); // Actual search criteria
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState<ForfeitureReasonDTO[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { search } = useForfeitureReasonSearch();
  const deleteForfeitureReason = useDeleteForfeitureReason();

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Forfeiture Reasons', href: '#' },
  ];

  const totalPages = Math.ceil(totalRecords / pageSize);

  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const response = await search({
        page: currentPage,
        pageSize,
        sortBy: 'CreatedUtc',
        sortDescending: true,
        filters: searchFilters,
      });
      setItems(response.results);
      setTotalRecords(response.totalCount);
    } catch (error) {
      console.error('Error fetching Forfeiture Reasons:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [currentPage, pageSize, searchFilters]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearchFilters(formFilters);
  };

  const handleClearSearch = () => {
    setFormFilters({});
    setSearchFilters({});
    setCurrentPage(1);
  };

  const handleDelete = async (dto: ForfeitureReasonDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await deleteForfeitureReason.mutateAsync(dto.id || '');
      await fetchItems();
    } catch (error) {
      console.error('Error deleting ForfeitureReason:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/forfeiturereasons"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission='Settings.ForfeitureReason.View'
    >
      <Card
        title="Manage Forfeiture Reasons"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton onClick={() => navigate('/admin/forfeiturereasons/create')} />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="title"
              label="Search by Title"
              value={formFilters.title || ''}
              onChange={(e) => setFormFilters({ ...formFilters, title: e.target.value })}
              placeholder="Search by Title"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <ForfeitureReasonTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/forfeiturereasons/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default ForfeitureReasonList;
